'use client'

import type React from 'react'
import { useState, useMemo, useCallback } from 'react'
import { motion } from 'framer-motion'
import { ChevronRight } from 'lucide-react'
import { Button } from '@/Components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card'
import { Badge } from '@/Components/ui/badge'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerDescription,
  DrawerTitle,
} from '@/Components/ui/drawer'
import { ScrollArea } from '@/Components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/Components/ui/select'
import { cn } from '@/lib/utils'
import type { Vas, Category, Translatable, LanguageCode } from '@/types'
import { useTranslation } from 'react-i18next'
import { LazySvg } from '@/Components/custom/lazy-svg'
import i18n from '@/lib/i18n'

const LocalizedText: React.FC<{ value: Translatable<string> | string | undefined }> = ({
  value,
}) => {
  const { i18n } = useTranslation()

  if (value === undefined) {
    return null
  }

  if (typeof value === 'string') {
    return <>{value}</>
  }

  return value[i18n.language as LanguageCode] || value.en || Object.values(value)[0] || ''
}

const getTranslation = (value: Translatable<string> | string | undefined): string | undefined => {
  const { i18n } = useTranslation()
  if (value === undefined) {
    return 'N/A'
  }

  if (typeof value === 'string') {
    return value
  }

  return value[i18n.language as LanguageCode] || value.en || Object.values(value)[0] || ''
}

const ServiceIcon: React.FC<{ name: string; className?: string }> = ({
  name,
  className = 'w-6 h-6 currentColor',
}) => {
  return <LazySvg containsPath={true} name={name} className={className} />
}

// BentoCard component directly adapted from enhanced-bento-grid
interface BentoCardProps {
  service: Vas
  className: string
  onClick: () => void
  index: number
}

function BentoCard({ service, className, onClick, index }: BentoCardProps) {
  const title = getTranslation(service.name)

  return (
    <motion.div
      className={`${className} bento-card group relative cursor-pointer overflow-hidden rounded-2xl shadow-md lg:rounded-[22px]`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{
        duration: 0.6,
        delay: index * 0.1,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      onClick={onClick}
    >
      {/* Background */}
      <div
        className="absolute inset-0 h-full w-full"
        style={{
          backgroundColor: service.image ? 'transparent' : service.color || '#f3f4f6',
        }}
      >
        {service.image && (
          <div className="absolute inset-0 h-full w-full overflow-hidden">
            <img
              src={service.image || '/placeholder.svg'}
              alt={typeof title === 'string' ? title : ''}
              className="h-full w-full object-cover object-center transition-all duration-300 ease-out group-hover:scale-110"
            />
          </div>
        )}

        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/30 transition-opacity duration-300 group-hover:bg-black/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex h-full flex-col justify-between p-4">
        <div className="flex items-start justify-between">
          <div
            className={`text-white transition-transform duration-300 ${!service.image ? 'group-hover:scale-110' : ''}`}
          >
            {service.icon && <ServiceIcon name={service.icon} className="h-8 w-8" />}
          </div>
          <div className="rounded-full bg-white/10 p-1 text-white transition-all duration-200 group-hover:bg-primary/80 rtl:rotate-180">
            <ChevronRight className="h-5 w-5" />
          </div>
        </div>

        <h3 className="mt-2 line-clamp-2 text-xl font-medium text-white transition-all duration-300 group-hover:text-[22px]">
          <LocalizedText value={service.name} />
        </h3>
      </div>
    </motion.div>
  )
}

// Main component
export default function BentoGrid({ services }: { services: Vas[] }) {
  const [selectedItem, setSelectedItem] = useState<Vas | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Animation variants
  const containerVariants = useMemo(
    () => ({
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.05,
        },
      },
      exit: {
        opacity: 0,
        transition: {
          staggerChildren: 0.03,
          staggerDirection: -1,
        },
      },
    }),
    []
  )

  // Handle main item click
  const handleItemClick = useCallback((item: Vas) => {
    setSelectedItem(item)
    if (item.categories) {
      const firstCategory = Object.values(item.categories)[0]
      setSelectedCategory(firstCategory)
    } else {
      setSelectedCategory(null)
    }
    setIsDrawerOpen(true)
  }, [])

  // Handle category click
  const handleCategoryClick = useCallback((category: Category) => {
    if (category) {
      setSelectedCategory(category)
    }
  }, [])

  // Directly use the enhanced-bento-grid layout
  const renderMainView = useMemo(
    () => (
      <div className="w-full">
        <div className="grid auto-rows-[minmax(120px,auto)] grid-cols-2 gap-3 sm:grid-cols-4 lg:aspect-[1120/800] lg:grid-cols-8 lg:grid-rows-[repeat(10,minmax(0,1fr))]">
          {/* First 4 columns (1-4) */}
          <div className="contents sm:col-span-4 lg:contents">
            {services.slice(0, Math.min(7, services.length)).map((service, index) => {
              // Define the className based on the index, matching the enhanced-bento-grid layout
              let cardClassName = ''

              switch (index) {
                case 0:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-3 lg:col-span-2 lg:row-span-3 lg:col-start-1 lg:row-start-1'
                  break
                case 1:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-4 lg:col-span-2 lg:row-span-4 lg:col-start-3 lg:row-start-1'
                  break
                case 2:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-2 lg:col-span-2 lg:row-span-2 lg:col-start-1 lg:row-start-4'
                  break
                case 3:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-2 lg:col-span-2 lg:row-span-2 lg:col-start-3 lg:row-start-5'
                  break
                case 4:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-3 lg:col-span-2 lg:row-span-3 lg:col-start-1 lg:row-start-6'
                  break
                case 5:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-2 lg:col-span-2 lg:row-span-2 lg:col-start-3 lg:row-start-7'
                  break
                case 6:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-4 sm:row-span-2 lg:col-span-4 lg:row-span-2 lg:col-start-1 lg:row-start-9'
                  break
                default:
                  cardClassName = 'col-span-2 row-span-1'
              }

              return (
                <BentoCard
                  key={service.key}
                  service={service}
                  onClick={() => handleItemClick(service)}
                  className={cardClassName}
                  index={index}
                />
              )
            })}
          </div>

          {/* Second 4 columns (5-8) */}
          <div className="contents sm:col-span-4 lg:contents">
            {services.slice(7, Math.min(16, services.length)).map((service, index) => {
              // Define the className based on the index, matching the enhanced-bento-grid layout
              let cardClassName = ''

              switch (index) {
                case 0:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-2 lg:col-span-2 lg:row-span-2 lg:col-start-5 lg:row-start-1'
                  break
                case 1:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-2 lg:col-span-2 lg:row-span-2 lg:col-start-7 lg:row-start-1'
                  break
                case 2:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-2 lg:col-span-2 lg:row-span-2 lg:col-start-5 lg:row-start-3'
                  break
                case 3:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-2 lg:col-span-2 lg:row-span-2 lg:col-start-7 lg:row-start-3'
                  break
                case 4:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-4 sm:row-span-2 lg:col-span-4 lg:row-span-2 lg:col-start-5 lg:row-start-5'
                  break
                case 5:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-2 lg:col-span-2 lg:row-span-2 lg:col-start-5 lg:row-start-7'
                  break
                case 6:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-2 lg:col-span-2 lg:row-span-2 lg:col-start-7 lg:row-start-7'
                  break
                case 7:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-2 lg:col-span-2 lg:row-span-2 lg:col-start-5 lg:row-start-9'
                  break
                case 8:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-2 lg:col-span-2 lg:row-span-2 lg:col-start-7 lg:row-start-9'
                  break
                default:
                  cardClassName =
                    'col-span-2 row-span-1 sm:col-span-2 sm:row-span-1 lg:col-span-2 lg:row-span-1 lg:col-start-5 lg:row-start-11'
              }

              return (
                <BentoCard
                  key={service.key}
                  service={service}
                  onClick={() => handleItemClick(service)}
                  className={cardClassName}
                  index={index + 7}
                />
              )
            })}
          </div>

          {/* Additional items beyond the first 16 */}
          {services.length > 16 && (
            <div className="col-span-2 mt-2 grid grid-cols-2 gap-3 sm:col-span-4 sm:grid-cols-4 lg:col-span-8 lg:grid-cols-8">
              {services.slice(16).map((service, index) => (
                <BentoCard
                  key={service.key}
                  service={service}
                  onClick={() => handleItemClick(service)}
                  className="col-span-2 row-span-1 sm:col-span-2 lg:col-span-2"
                  index={index + 16}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    ),
    [services, handleItemClick]
  )

  // get from the selecteItem services and from first service's attributes first attribute if attribute.category === 'description' get value
  // and return it as LocalizedText

  const getDescription = useCallback(() => {
    if (!selectedItem) return null

    const services = selectedCategory ? selectedCategory.services : selectedItem.services

    if (!services || !services[0]) return null

    const description = services[0].attributes.find(
      (attr) => attr.category === 'description'
    )?.value

    // Handle the different types that could be in the description value
    if (!description) return null

    // Convert string[] to string if necessary
    if (Array.isArray(description)) {
      return <LocalizedText value={description[0] || ''} />
    }

    return <LocalizedText value={description} />
  }, [selectedItem, selectedCategory])

  // Render categories - keeping original functionality
  const renderCategories = useMemo(() => {
    if (!selectedItem || !selectedItem.categories) return null

    const categories = Object.values(selectedItem.categories)

    return (
      <>
        <div className="mb-1 mt-2 hidden space-x-2 overflow-x-auto md:flex rtl:space-x-reverse rtl:md:flex-row">
          {categories.map((category, idx) => (
            <motion.div
              key={category.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  delay: idx * 0.05,
                  duration: 0.3,
                  ease: [0.215, 0.61, 0.355, 1],
                },
              }}
            >
              <Button
                variant={selectedCategory === category ? 'default' : 'outline'}
                onClick={() => handleCategoryClick(category)}
                className="flex-shrink-0 rounded-lg border-border shadow-none hover:shadow"
              >
                <LocalizedText value={category.name} />
              </Button>
            </motion.div>
          ))}
        </div>
        <div className="mb-1 w-full md:hidden">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.3,
                ease: [0.215, 0.61, 0.355, 1],
              },
            }}
          >
            <Select
              value={selectedCategory?.key}
              onValueChange={(value) =>
                /* @ts-ignore */
                handleCategoryClick(categories.find((category) => category.key === value))
              }
              dir={i18n.dir()}
            >
              <SelectTrigger className="mx-auto mt-2 h-12 w-full max-w-md rounded-xl border-border">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.key} value={category.key}>
                    <LocalizedText value={category.name} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </motion.div>
        </div>
      </>
    )
  }, [selectedItem, selectedCategory, handleCategoryClick])

  // Render services - keeping original functionality
  const renderServices = useMemo(() => {
    if (!selectedItem) return null

    const services = selectedCategory ? selectedCategory.services : selectedItem.services

    if (!services) return null

    return (
      <motion.div
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        dir={i18n.dir()}
      >
        {services.map((service, idx) => (
          <motion.div
            key={service.id}
            initial={{
              opacity: 0,
              y: 15,
            }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                type: 'spring',
                stiffness: 60,
                damping: 15,
                delay: idx * 0.04,
              },
            }}
            whileHover={{
              y: -3,
              transition: { duration: 0.2 },
            }}
          >
            <Card
              className={cn(
                'h-full overflow-hidden rounded-[22px] text-foreground/95 shadow-sm hover:border-border/40 hover:shadow hover:shadow-primary/30 dark:bg-black'
              )}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl font-bold">
                    <LocalizedText value={service.title} />
                  </CardTitle>
                  <div className="inline-flex items-center text-primary">
                    <LazySvg name="/icons/currency-alt.svg" className="size-5" />
                    <div className="text-2xl font-bold">{service.price}</div>
                  </div>
                </div>
                <div>
                  <Badge variant="destructive-outline" className="mt-1 font-normal">
                    <LocalizedText value={service.validity} />
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="pt-4">
                <div className="space-y-4">
                  {service.attributes.map(
                    (attr) =>
                      attr.category !== 'description' && (
                        <div key={attr.id} className="flex items-center gap-3">
                          {attr.settings.icon && (
                            <div className="self-start rounded-full bg-primary/10 p-1.5 dark:bg-primary/20">
                              <ServiceIcon
                                name={attr.settings.icon}
                                className="size-4 text-primary"
                              />
                            </div>
                          )}
                          <div className="space-y-0.5">
                            {attr.settings.name && (
                              <h5 className="text-sm font-medium capitalize">
                                <LocalizedText value={attr.settings.name} />
                              </h5>
                            )}
                            <div className="text-sm text-muted-foreground">
                              {/* @ts-ignore */}
                              <LocalizedText value={attr.value} />
                            </div>
                          </div>
                        </div>
                      )
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    )
  }, [selectedItem, selectedCategory, containerVariants])

  return (
    <>
      {renderMainView}

      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerContent
          className="max-h-[90vh] dark:bg-black"
          aria-describedby="drawer-description"
          aria-description="drawer-description"
        >
          <div className="sticky top-0 z-10 border-b border-border">
            <DrawerHeader className="pb-0">
              <DrawerTitle className="text-center text-2xl font-bold">
                {selectedItem && (
                  <div className="flex items-center justify-center gap-x-2">
                    {selectedItem.icon && (
                      <div className="flex size-8 items-center justify-center rounded-md bg-primary/10 ring-1 ring-primary/20 dark:bg-primary/30 dark:ring-primary/40">
                        <ServiceIcon
                          name={selectedItem.icon || ''}
                          className="size-6 text-primary"
                        />
                      </div>
                    )}
                    <LocalizedText value={selectedItem.name} />
                  </div>
                )}
              </DrawerTitle>

              <DrawerDescription className="sr-only">Service Description</DrawerDescription>
            </DrawerHeader>
            <div className="flex items-center justify-center px-6 pb-4">{renderCategories}</div>
          </div>
          <ScrollArea className="h-[calc(90vh-8rem)]">
            <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-6" dir={i18n.dir()}>
              <p className="text-base first-letter:text-xl first-letter:font-bold first-letter:text-primary rtl:tracking-wide">
                {getDescription()}
              </p>
              <div>{renderServices}</div>
            </div>
          </ScrollArea>
        </DrawerContent>
      </Drawer>
    </>
  )
}
